import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ThemeToggle } from "@/components/ThemeToggle";
import { NavLangSwitch } from "@/components/site/LangToggle";
import { Menu, X, ArrowUpRight, ChevronDown, UserRound, Layers, Gavel, TrendingUp, Building2, Send } from "lucide-react";
import { CORE_SERVICES, MORE_SERVICES } from "../../data/services";
import { useAuth } from "@/context/AuthContext";
import { LogOut as LogOutIcon } from "lucide-react";
import { servicePath } from "@/lib/paths";
import { SiteSearch } from "./SiteSearch";
import { PodcastMark } from "./PodcastMark";
import { NexusMark } from "./NexusMark";
import { FestiveLogoMark, FestiveTopLine, LogoWishLine } from "./FestiveLogo";
import { MAP_LINKS } from "../../data/maps";

const ANCHORS = [
  { label: "Blogs", href: "/insights" },
  { label: "News", href: "/news" },
  { label: "About Us", href: "/aboutus" },
  { label: "Team", href: "/team" },
  { label: "Impact", href: "/impact" },
  { label: "FAQ", href: "/faq" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [svcOpen, setSvcOpen] = useState(false);
  const [mktOpen, setMktOpen] = useState(false);
  const [coOpen, setCoOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setSvcOpen(false); }, [location.pathname]);

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-gf-bg/70 backdrop-blur-xl border-b border-white/5" : "border-b border-transparent"
      }`}
    >
      <FestiveTopLine />
      <div className="max-w-[1600px] mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between gap-4">
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-2.5 group shrink-0">
          <FestiveLogoMark />
          <img src="/growthifye-emblem.png" alt="Growthifye" className="h-8 w-auto" />
          <span className="font-heading font-black text-xl tracking-tight text-gf-text whitespace-nowrap">
            growthify<span className="text-gf-primary">e</span>
          </span>
        </Link>
        <LogoWishLine lang={location.pathname.startsWith("/hi") ? "hi" : "en"} />

        <nav className="hidden lg:flex items-center gap-2 xl:gap-3 2xl:gap-6 px-2 min-w-0 flex-1 justify-start xl:justify-center [overflow-x:clip] overflow-y-visible">
          <div className="relative" onMouseEnter={() => setSvcOpen(true)} onMouseLeave={() => setSvcOpen(false)}>
            <button data-testid="nav-services" onClick={() => setSvcOpen((v) => !v)}
              className="flex items-center gap-1.5 font-body text-sm text-gf-muted hover:text-gf-text transition-colors duration-200">
              <Link to="/coreservices" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1.5"><Layers className="w-4 h-4 text-gf-primary/80" /> Services</Link>
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${svcOpen ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {svcOpen && (
                <motion.div
                  data-testid="services-dropdown"
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.18 }}
                  className="absolute left-0 top-full pt-4 w-[640px] max-w-[calc(100vw-40px)]"
                >
                  <div className="bg-gf-surface border border-white/10 shadow-2xl p-7 grid grid-cols-2 gap-8">
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gf-primary">Core services</p>
                      <ul className="mt-4 space-y-1">
                        {CORE_SERVICES.map((s) => (
                          <li key={s.slug}>
                            <Link to={servicePath(s.slug)} data-testid={`nav-service-${s.slug}`}
                              className="block font-body text-sm text-gf-text/85 hover:text-gf-primary py-1.5 transition-colors">
                              {s.short}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gf-muted">More services</p>
                      <ul className="mt-4 space-y-1">
                        {MORE_SERVICES.map((s) => (
                          <li key={s.slug}>
                            <Link to={servicePath(s.slug)} data-testid={`nav-service-${s.slug}`}
                              className="block font-body text-sm text-gf-text/85 hover:text-gf-primary py-1.5 transition-colors">
                              {s.short}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <Link to="/marketpulse" data-testid="nav-market-pulse"
            className="flex items-center gap-1.5 font-body text-sm font-bold text-gf-primary border border-gf-primary/40 px-3 py-1 rounded-full hover:bg-gf-primary/10 transition-colors duration-200 whitespace-nowrap">
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gf-primary opacity-70" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-gf-primary" />
            </span>
            Market Pulse
          </Link>

          <Link to="/tenders" data-testid="nav-tenders-top"
            className="hidden 2xl:inline-flex items-center gap-1.5 font-body text-sm text-gf-muted hover:text-gf-text transition-colors duration-200 whitespace-nowrap">
            <Gavel className="w-4 h-4 text-gf-primary/80" /> Tenders
          </Link>

          <Link to="/podcast" data-testid="nav-podcast-top"
            className="hidden xl:inline-flex items-center gap-1.5 font-body text-sm text-gf-muted hover:text-gf-text transition-colors duration-200 whitespace-nowrap">
            <PodcastMark className="w-4 h-4" title="" /> Podcast
          </Link>

          <Link to="/nexus" data-testid="nav-saas-top"
            className="inline-flex items-center gap-1.5 font-body text-sm font-bold text-gf-text border border-white/20 px-3 py-1 rounded-full hover:border-gf-primary hover:text-gf-primary transition-colors duration-200 whitespace-nowrap">
            <NexusMark className="w-4 h-4 text-gf-primary" title="" /> SaaS
          </Link>

          <div className="relative" onMouseEnter={() => setMktOpen(true)} onMouseLeave={() => setMktOpen(false)}>
            <button data-testid="nav-markets" onClick={() => setMktOpen((v) => !v)}
              className="flex items-center gap-1.5 font-body text-sm text-gf-muted hover:text-gf-text transition-colors duration-200 whitespace-nowrap">
              <TrendingUp className="w-4 h-4 text-gf-primary/80" /> Markets <ChevronDown className={`w-3.5 h-3.5 transition-transform ${mktOpen ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {mktOpen && (
                <motion.div data-testid="markets-dropdown"
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.18 }}
                  className="absolute right-0 top-full pt-4 w-[300px]">
                  <div className="bg-gf-surface border border-white/10 shadow-2xl p-3">
                    <Link to="/market-prices" data-testid="nav-market-prices" onClick={() => setMktOpen(false)}
                      className="block font-body text-sm text-gf-text/85 hover:text-gf-primary px-2 py-2 transition-colors">
                      Market Prices</Link>
                    <Link to="/tenders" data-testid="nav-tenders" onClick={() => setMktOpen(false)}
                      className="flex items-center gap-1.5 font-body text-sm text-gf-text/85 hover:text-gf-primary px-2 py-2 transition-colors">
                      <span className="relative flex h-1.5 w-1.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gf-primary opacity-70" />
                        <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-gf-primary" />
                      </span>
                      Tenders</Link>
                    <Link to="/intel/bess" data-testid="nav-intel" onClick={() => setMktOpen(false)}
                      className="block font-body text-sm text-gf-text/85 hover:text-gf-primary px-2 py-2 transition-colors">
                      Industry Intel</Link>
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gf-primary px-2 pt-3 pb-1 border-t border-white/10 mt-1">Tariff & obligation desks</p>
                    {[["/intel/auction-tariffs", "nav-auction-tariffs", "Tariff Estimates · e-RA desk"], ["/markets/pm-kusum", "nav-pm-kusum", "PM-KUSUM desk"], ["/markets/solar-pumps", "nav-solar-pumps", "Solar Water Pumps desk"], ["/markets/rpo", "nav-rpo", "RPO · HPO · Storage obligations & APPC"]].map(([to, tid, l]) => (
                      <Link key={to} to={to} data-testid={tid} onClick={() => setMktOpen(false)}
                        className="block font-body text-sm text-gf-text/85 hover:text-gf-primary px-2 py-2 transition-colors">{l}</Link>
                    ))}
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gf-primary px-2 pt-3 pb-1 border-t border-white/10 mt-1">Infra & Intel Maps</p>
                    {MAP_LINKS.map((m) => (
                      <Link key={m.key} to={m.to} data-testid={`nav-map-${m.key}`} onClick={() => setMktOpen(false)}
                        className="block font-body text-sm text-gf-text/85 hover:text-gf-primary px-2 py-2 transition-colors">
                        {m.label}</Link>
                    ))}
                    <Link to="/besssizing" data-testid="nav-bess-sizing" onClick={() => setMktOpen(false)}
                      className="block font-body text-sm text-gf-text/85 hover:text-gf-primary px-2 py-2 transition-colors">
                      Bill Optimiser</Link>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="relative" onMouseEnter={() => setCoOpen(true)} onMouseLeave={() => setCoOpen(false)}>
            <button data-testid="nav-company" onClick={() => setCoOpen((v) => !v)}
              className="flex items-center gap-1.5 font-body text-sm text-gf-muted hover:text-gf-text transition-colors duration-200 whitespace-nowrap">
              <Building2 className="w-4 h-4 text-gf-primary/80" /> Company <ChevronDown className={`w-3.5 h-3.5 transition-transform ${coOpen ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {coOpen && (
                <motion.div data-testid="company-dropdown"
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.18 }}
                  className="absolute right-0 top-full pt-4 w-[200px]">
                  <div className="bg-gf-surface border border-white/10 shadow-2xl p-3">
                    {ANCHORS.filter((l) => !["Impact", "FAQ"].includes(l.label)).map((l) => (
                      <Link key={l.href} to={l.href} data-testid={`nav-${l.label.toLowerCase()}`}
                        onClick={() => setCoOpen(false)}
                        className="block font-body text-sm text-gf-text/85 hover:text-gf-primary px-2 py-2 transition-colors">
                        {l.label}
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </nav>

        <div className="flex items-center gap-2 md:gap-3 shrink-0">
          <a href="/#contact" data-testid="nav-cta"
            className="hidden xl:inline-flex items-center gap-1.5 whitespace-nowrap bg-gf-primary text-gf-bg font-body font-bold text-sm px-4 py-2.5 hover:bg-gf-primary-hover transition-colors duration-200">
            <Send className="w-4 h-4" /> <span className="hidden min-[1400px]:inline">Get in touch</span><span className="min-[1400px]:hidden">Contact</span> <ArrowUpRight className="w-4 h-4" />
          </a>

          <SiteSearch />
          <NavLangSwitch />
          <span className="hidden 2xl:block"><ThemeToggle /></span>

          <div className="relative group">
          <button data-testid="nav-login"
            className="inline-flex items-center gap-1.5 border border-white/20 text-gf-text font-body font-bold text-sm px-3 md:px-4 py-2.5 hover:border-gf-primary hover:text-gf-primary transition-colors duration-200">
            <UserRound className="w-4 h-4" /> <span className="hidden sm:inline">{user ? (user.name || "Account").split(" ")[0] : "Login"}</span>
            <ChevronDown className="w-3.5 h-3.5" />
          </button>
          <div className="absolute right-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible transition-all duration-150 z-50">
            <div className="bg-gf-bg/95 backdrop-blur-xl border border-white/10 min-w-[190px]">
              <Link to="/login" data-testid="nav-login-team"
                className="flex items-center gap-2 px-4 py-3 font-body text-sm text-gf-text hover:text-gf-primary hover:bg-white/[0.04] transition-colors">
                <UserRound className="w-4 h-4 text-gf-primary" /> Team login
              </Link>
              <Link to="/clientportal" data-testid="nav-login-client"
                className="flex items-center gap-2 px-4 py-3 font-body text-sm text-gf-text hover:text-gf-primary hover:bg-white/[0.04] border-t border-white/10 transition-colors">
                <ArrowUpRight className="w-4 h-4 text-gf-primary" /> Client portal
              </Link>
              {user && (
                <button data-testid="nav-signout" onClick={logout}
                  className="w-full flex items-center gap-2 px-4 py-3 font-body text-sm text-gf-text hover:text-gf-primary hover:bg-white/[0.04] border-t border-white/10 transition-colors">
                  <LogOutIcon className="w-4 h-4 text-gf-primary" /> Sign out
                </button>
              )}
            </div>
          </div>
        </div>

          <button data-testid="mobile-menu-toggle" className="lg:hidden text-gf-text p-2"
            onClick={() => setOpen((v) => !v)} aria-label="Toggle menu">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden bg-gf-bg/95 backdrop-blur-xl border-b border-white/5 max-h-[calc(100vh-72px)] overflow-y-auto"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              <div className="flex items-center justify-between gap-3">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gf-primary">Services</p>
                <NavLangSwitch compact onNavigate={() => setOpen(false)} />
              </div>
              {[...CORE_SERVICES, ...MORE_SERVICES].map((s) => (
                <Link key={s.slug} to={servicePath(s.slug)} onClick={() => setOpen(false)}
                  className="font-heading font-bold text-lg text-gf-text">
                  {s.short}
                </Link>
              ))}
              <div className="border-t border-white/10 pt-5 flex flex-col gap-5">
                <Link to="/marketpulse" onClick={() => setOpen(false)} data-testid="mobile-nav-market-pulse" className="font-heading font-bold text-2xl text-gf-primary">
                  Market Pulse <span className="text-gf-primary text-sm align-middle">● LIVE PRICES</span>
                </Link>
                <Link to="/nexus" onClick={() => setOpen(false)} data-testid="mobile-nav-saas" className="font-heading font-bold text-2xl text-gf-text inline-flex items-center gap-2">
                  <NexusMark className="w-6 h-6 text-gf-primary" title="" /> Nexus SaaS <span className="text-gf-primary text-sm align-middle">● 14 MODULES</span>
                </Link>
                <Link to="/tenders" onClick={() => setOpen(false)} data-testid="mobile-nav-tenders" className="font-heading font-bold text-2xl text-gf-text">
                  Tenders
                </Link>
                <Link to="/besssizing" onClick={() => setOpen(false)} className="font-heading font-bold text-2xl text-gf-text">
                  Bill Optimiser
                </Link>
                <Link to="/market-prices" onClick={() => setOpen(false)} data-testid="mobile-nav-market-prices" className="font-heading font-bold text-2xl text-gf-text">
                  Market Prices
                </Link>
                <Link to="/intel/bess" onClick={() => setOpen(false)} data-testid="mobile-nav-intel" className="font-heading font-bold text-2xl text-gf-text">
                  Industry Intel <span className="text-gf-primary text-sm align-middle">● DAILY</span>
                </Link>
                <Link to="/intel/auction-tariffs" onClick={() => setOpen(false)} data-testid="mobile-nav-auction-tariffs" className="font-heading font-bold text-2xl text-gf-text">
                  Tariff Estimates · e-RA</Link>
                <Link to="/infra-intel" onClick={() => setOpen(false)} data-testid="mobile-nav-infra-intel" className="font-heading font-bold text-2xl text-gf-text">
                  Infra & Intel Maps <span className="text-gf-primary text-sm align-middle">● 5 MAPS</span>
                </Link>
                <Link to="/tenders" onClick={() => setOpen(false)} className="font-heading font-bold text-2xl text-gf-text">
                  Tenders <span className="text-gf-primary text-sm align-middle">● LIVE</span>
                </Link>
                <Link to="/careers" onClick={() => setOpen(false)} data-testid="mobile-nav-careers" className="font-heading font-bold text-2xl text-gf-text">
                  Careers
                </Link>
                <Link to="/contactus" onClick={() => setOpen(false)} data-testid="mobile-nav-contact" className="font-heading font-bold text-2xl text-gf-text">
                  Contact Us
                </Link>
                {ANCHORS.map((l) => (
                  <Link key={l.href} to={l.href} onClick={() => setOpen(false)} className="font-heading font-bold text-2xl text-gf-text">
                    {l.label}
                  </Link>
                ))}
                <Link to="/login" onClick={() => setOpen(false)} data-testid="mobile-nav-login" className="font-heading font-bold text-2xl text-gf-text">
                  Team login <UserRound className="inline w-5 h-5 text-gf-primary align-middle" />
                </Link>
                <Link to="/clientportal" onClick={() => setOpen(false)} data-testid="mobile-nav-client-login" className="font-heading font-bold text-2xl text-gf-text">
                  Client portal <ArrowUpRight className="inline w-5 h-5 text-gf-primary align-middle" />
                </Link>
              </div>
              <a href="/#contact" onClick={() => setOpen(false)}
                className="mt-2 inline-flex items-center justify-center gap-1.5 bg-gf-primary text-gf-bg font-bold px-5 py-3">
                Get in touch <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
